import { AUTH_CONFIG } from "../config/authConfig";
import { BILLING_PATH } from "./subscriptionAccess";
import { getDefaultAuthorizedPath } from "./permissions";

export const LOGIN_PATH = AUTH_CONFIG.REDIRECTS.LOGIN;
export const DEFAULT_POST_LOGIN_PATH = AUTH_CONFIG.REDIRECTS.DASHBOARD;
export const RETURN_URL_PARAM = AUTH_CONFIG.REDIRECTS.RETURN_URL_PARAM;

/**
 * Validates an internal app path for post-login redirect (open-redirect safe).
 */
export const isSafeInternalPath = (path) => {
  if (!path || typeof path !== "string") {
    return false;
  }

  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return false;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return false;
  }

  const pathname = trimmed.split(/[?#]/)[0].toLowerCase();
  if (pathname === "/user/login" || pathname.startsWith("/user/login/")) {
    return false;
  }

  return true;
};

/**
 * Builds a full path from a React Router location object.
 */
export const locationToPath = (loc) => {
  if (!loc) {
    return "";
  }
  return `${loc.pathname || ""}${loc.search || ""}${loc.hash || ""}`;
};

/**
 * State payload for Navigate/navigate to login while preserving intended destination.
 */
export const buildLoginRedirectState = (fromLocation) => ({
  state: { from: fromLocation },
});

/**
 * Resolves where to send the user after successful login.
 * Priority: billing (if restricted) > location.state.from > returnUrl > company-aware default.
 */
export const getPostLoginPath = ({
  location,
  searchParams,
  subscriptionAccess,
  user,
} = {}) => {
  if (subscriptionAccess?.isRestricted) {
    return BILLING_PATH;
  }

  const fromState = location?.state?.from;
  if (fromState) {
    const pathFromState = locationToPath(fromState);
    if (isSafeInternalPath(pathFromState)) {
      return pathFromState;
    }
  }

  if (searchParams) {
    const returnUrl = searchParams.get(RETURN_URL_PARAM);
    if (returnUrl && isSafeInternalPath(returnUrl)) {
      return returnUrl;
    }
  }

  if (user) {
    return getDefaultAuthorizedPath(user);
  }

  return DEFAULT_POST_LOGIN_PATH;
};

/**
 * Navigate to login with replace, preserving the attempted route in state.
 */
export const navigateToLogin = (navigate, fromLocation, options = {}) => {
  const { replace = true } = options;
  navigate(LOGIN_PATH, {
    replace,
    ...buildLoginRedirectState(fromLocation),
  });
};
